import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    useTheme,
    Tooltip,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack,
    Snackbar,
    Breadcrumbs,
    Link,
    Autocomplete
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    HolidayVillage,
    HowToVote,
    Person,
    ArrowBack,
    NavigateNext,
    Lock
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import api from '../../api/axios';

interface MasterVillage {
    id: number;
    name: string;
    mandal?: { id: number; name: string };
    mandal_id?: number;
    voting_booths_count: number;
    citizens_count: number;
}

const MasterConstituencyDetail = () => {
    const { masterConstituencyId } = useParams<{ masterConstituencyId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const theme = useTheme();

    // State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<MasterVillage | null>(null);
    const [formData, setFormData] = useState({ name: '', mandal_id: '' });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [search, setSearch] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MasterVillage | null>(null);

    // Fetch Constituency Details
    const { data: constituency, isLoading: loadingConstituency } = useQuery({
        queryKey: ['master-constituency', masterConstituencyId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/master-constituencies/${masterConstituencyId}`);
            return res.data?.data || res.data;
        },
        enabled: !!masterConstituencyId,
    });

    // Fetch Villages
    const { data: villagesData, isLoading: loadingVillages } = useQuery({
        queryKey: ['master-villages', masterConstituencyId, search],
        queryFn: async () => {
            const params: any = { master_constituency_id: masterConstituencyId, per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/superadmin/master-villages', { params });
            return res.data;
        },
        enabled: !!masterConstituencyId,
        placeholderData: keepPreviousData,
    });

    const villages = villagesData?.data?.data || villagesData?.data || [];

    // Fetch Districts for form
    const { data: districts = [] } = useQuery({
        queryKey: ['districts-list'],
        queryFn: async () => {
            const res = await api.get('/superadmin/districts?per_page=100');
            return res.data?.data?.data || res.data?.data || [];
        },
    });

    const [selectedDistrictId, setSelectedDistrictId] = useState<string>('');

    // Fetch Mandals for form (filtered by district if selected)
    const { data: mandals = [] } = useQuery({
        queryKey: ['mandals-list', selectedDistrictId],
        queryFn: async () => {
            const params: any = { per_page: 100 };
            if (selectedDistrictId) {
                params.district_id = selectedDistrictId;
            }
            const res = await api.get('/superadmin/mandals', { params });
            return res.data?.data?.data || res.data?.data || [];
        },
    });

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: any) => api.post('/superadmin/master-villages', {
            ...data,
            master_constituency_id: Number(masterConstituencyId),
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-villages'] });
            queryClient.invalidateQueries({ queryKey: ['master-constituency'] });
            handleClose();
            showSnackbar('Village created successfully', 'success');
        },
        onError: (err: any) => {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            } else {
                showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
            }
        }
    });

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/superadmin/master-villages/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-villages'] });
            handleClose();
            showSnackbar('Village updated successfully', 'success');
        },
        onError: (err: any) => {
            showSnackbar(err.response?.data?.message || 'Failed to update village', 'error');
        }
    });

    // Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/master-villages/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-villages'] });
            queryClient.invalidateQueries({ queryKey: ['master-constituency'] });
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            showSnackbar('Village deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete village', 'error')
    });

    const handleOpen = (item?: MasterVillage) => {
        if (item) {
            setEditingItem(item);
            setFormData({ name: item.name, mandal_id: item.mandal_id ? String(item.mandal_id) : '' });
            // Ideally we would set selectedDistrictId here if we had the data
            // For now, we leave it empty so user can re-select if needed, 
            // or we could fetch the mandal details to get the district_id
            if (item.mandal && (item.mandal as any).district_id) {
                setSelectedDistrictId(String((item.mandal as any).district_id));
            } else {
                setSelectedDistrictId('');
            }
        } else {
            setEditingItem(null);
            setFormData({ name: '', mandal_id: '' });
            setSelectedDistrictId('');
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ name: '', mandal_id: '' });
        setSelectedDistrictId('');
    };

    const handleSubmit = () => {
        if (!formData.name) {
            setFormErrors({ name: 'Name is required' });
            return;
        }

        const payload = {
            name: formData.name,
            mandal_id: formData.mandal_id ? Number(formData.mandal_id) : null,
        };

        if (editingItem) {
            updateMutation.mutate({ ...payload, id: editingItem.id });
        } else {
            createMutation.mutate(payload);
        }
    };

    const handleDeleteClick = (item: MasterVillage) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            deleteMutation.mutate(itemToDelete.id);
        }
    };

    const isLoading = loadingConstituency || loadingVillages;
    const totalVillages = villages.length;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/super-admin/master-constituencies" sx={{ display: 'flex', alignItems: 'center' }}>
                        Master Constituencies
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {constituency?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate('/super-admin/master-constituencies')}
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        {constituency?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Manage villages within this constituency
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 7 }}>
                        <Chip
                            label={`${totalVillages} Villages`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                        <Chip
                            label={`${constituency?.citizens_count || 0} Citizens`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                    </Stack>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpen()}
                    sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                    }}
                >
                    Add Village
                </Button>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Villages List
                </Typography>
                <TextField
                    placeholder="Search villages..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 200, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={44} height={44} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="60%" />
                                            <Skeleton variant="text" width="40%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (villages as MasterVillage[]).length > 0 ? (
                    (villages as MasterVillage[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                                onClick={() => navigate(`/super-admin/master-constituencies/${masterConstituencyId}/villages/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                                color: '#10b981',
                                                width: 44,
                                                height: 44,
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem'
                                            }}
                                        >
                                            {item.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpen(item);
                                                    }}
                                                    sx={{ color: theme.palette.warning.main }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    sx={{ color: theme.palette.error.main }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
                                        {item.name}
                                    </Typography>
                                    {item.mandal && (
                                        <Chip
                                            size="small"
                                            label={item.mandal.name}
                                            sx={{ mb: 2, bgcolor: '#f3f4f6' }}
                                        />
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <HowToVote fontSize="small" color="primary" />
                                                <Typography variant="caption">{item.voting_booths_count || 0} Booths</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Person fontSize="small" color="success" />
                                                <Typography variant="caption">{item.citizens_count || 0} Citizens</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <HolidayVillage sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No villages found matching "${search}"` : "No villages in this constituency yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Add villages to organize voting booths and citizens
                            </Typography>
                            {!search && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => handleOpen()}
                                >
                                    Add First Village
                                </Button>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Edit Village' : 'Add New Village'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            label="Village Name"
                            fullWidth
                            variant="outlined"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            disabled={createMutation.isPending || updateMutation.isPending}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><HolidayVillage color="action" /></InputAdornment>,
                            }}
                            sx={{ mb: 2 }}
                        />
                        <Autocomplete
                            options={districts}
                            getOptionLabel={(option: any) => option.name}
                            value={districts.find((d: any) => String(d.id) === selectedDistrictId) || null}
                            onChange={(_, v) => {
                                setSelectedDistrictId(v?.id ? String(v.id) : '');
                                // Clear selected mandal if district changes
                                setFormData({ ...formData, mandal_id: '' });
                            }}
                            renderInput={(params) => <TextField {...params} label="Filter Mandals by District (Optional)" />}
                            sx={{ mb: 2 }}
                        />
                        <Autocomplete
                            options={mandals}
                            getOptionLabel={(option: any) => option.name}
                            value={mandals.find((m: any) => m.id === Number(formData.mandal_id)) || null}
                            onChange={(_, v) => setFormData({ ...formData, mandal_id: v?.id ? String(v.id) : '' })}
                            renderInput={(params) => <TextField {...params} label={selectedDistrictId ? "Select Mandal (Filtered)" : "Select Mandal (All)"} />}
                            disabled={!selectedDistrictId && mandals.length > 100} // Optional UX choice: force district selection if too many mandals? No, just let it be.
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }} size="large">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                        size="large"
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Village'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: This will also delete all associated voting booths and citizens!
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MasterConstituencyDetail;
